import { useState, useEffect, useCallback } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Check from '@mui/icons-material/Check';
import 'react-circular-progressbar/dist/styles.css';
import WorkflowGenerationProgress from './WorkflowGenerationProgress';
import { getRequest, postRequest, putRequest, showMessage } from '../../../config';

// ── Workflow generation persistence ──────────────────────────────────────────
const WF_KEY = (cid) => `wf_progress_${cid}`;
const WF_TIMEOUT_MS = 15 * 60 * 1000; // 15 min safety — abandon if server died

const readWfProgress = (cid) => {
    if (!cid) return null;
    try {
        const raw = localStorage.getItem(WF_KEY(cid));
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - (data.startedAt || 0) > WF_TIMEOUT_MS) { localStorage.removeItem(WF_KEY(cid)); return null; }
        return data;
    } catch { return null; }
};
const writeWfProgress = (cid, patch) => {
    if (!cid) return;
    try { localStorage.setItem(WF_KEY(cid), JSON.stringify({ ...(readWfProgress(cid) || {}), ...patch })); } catch { }
};
const clearWfProgress = (cid) => { if (cid) try { localStorage.removeItem(WF_KEY(cid)); } catch { } };
// ─────────────────────────────────────────────────────────────────────────────

const PRIORITY_OPTIONS = [
    { value: 'High', label: 'High Priority', bg: '#F24E4E', color: '#fff' },
    { value: 'Medium', label: 'Medium Priority', bg: '#FF8C00', color: '#fff' },
    { value: 'Low', label: 'Low Priority', bg: '#FFD700', color: '#333' },
];

const STATUS_OPTIONS = [
    { value: 'Not Started', label: 'Not Started', bg: '#F5F6F9', color: '#505050', border: '1px solid #D9D9D9' },
    { value: 'In Progress', label: 'In Progress', bg: '#FFD700', color: '#333' },
    { value: 'Ready For Review', label: 'Ready For Review', bg: '#3DD68C', color: '#fff', check: true },
    { value: 'Completed', label: 'Completed', bg: '#09B255', color: '#fff', check: true },
    { value: 'Not Applicable', label: 'Not Applicable', bg: '#E8EBF5', color: '#505050', border: '1px solid #D9D9D9' },
];

const TABLE_COLS = ['Source / Question(s)', 'Issue Name', 'Reason', 'Investor Lens', 'Criticality', 'Action Required', 'Priority', 'Assigned To', 'Status'];
const ITEMS_PER_PAGE = 10;

const getCriticalityIcon = (level) => {
    const icon = level === 'high'
        ? require('../../../../images/featureWorkflow/high warning.svg')
        : level === 'medium'
            ? require('../../../../images/featureWorkflow/medium warning.svg')
            : require('../../../../images/featureWorkflow/low warning.svg');
    return <img src={icon} alt={level} style={{ width: '20px', height: '20px' }} />;
};

function ModuleSelectModal({ eligibleMods, preGenSelectedIds, setPreGenSelectedIds, modalProgressLoading, modalProgress, handleConfirmGenerate, onClose }) {
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    const allSelected = eligibleMods.length > 0 && eligibleMods.every(m => preGenSelectedIds.has(m['_id'].toString()));
    const toggleMod = (id) => {
        const s = new Set(preGenSelectedIds);
        s.has(id) ? s.delete(id) : s.add(id);
        setPreGenSelectedIds(s);
    };
    const toggleAll = () => {
        if (allSelected) setPreGenSelectedIds(new Set());
        else setPreGenSelectedIds(new Set(eligibleMods.map(m => m['_id'].toString())));
    };
    return (
        <div className="pf t0 l0 w100 df aic jcc" style={{ height: '100vh', backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', zIndex: 10000 }}
            onClick={onClose}>
            <div className="bg1 br20px shadow df fdc" style={{ width: '480px', maxHeight: '80vh', overflow: 'hidden', border: '1px solid #DDE2F2' }}
                onClick={e => e.stopPropagation()}>
                <div className="px2rem pt2rem pb1rem" style={{ borderBottom: '1px solid #DDE2F2' }}>
                    <h3 className="fs1rem fw600 c15 mb0-25rem">Select Modules to Analyse</h3>
                    <p className="fs0-8rem fw400 c12">Choose one or more modules to generate a priority list for.</p>
                </div>
                {modalProgressLoading ? (
                    <div className="df aic jcc py3rem"><p className="fs0-875rem fw400 c12">Loading module progress…</p></div>
                ) : (
                    <>
                        <div className="oya px2rem py1rem" style={{ flex: 1 }}>
                            <div className="df aic jcsb py0-5rem mb0-5rem cp" style={{ borderBottom: '1px solid #F5F6F9' }} onClick={toggleAll}>
                                <span className="fs0-875rem fw500 c15">Select All</span>
                                <div className="df aic jcc br5px ml1rem" style={{ width: 18, height: 18, border: `2px solid ${allSelected ? '#4A67FF' : '#D9D9D9'}`, backgroundColor: allSelected ? '#4A67FF' : '#fff', flexShrink: 0 }}>
                                    {allSelected && <Check style={{ fontSize: 12, color: '#fff' }} />}
                                </div>
                            </div>
                            {eligibleMods.map(mod => {
                                const id = mod['_id'].toString();
                                const checked = preGenSelectedIds.has(id);
                                const answered = modalProgress?.[id] || 0;
                                const isStarted = answered > 0;
                                return (
                                    <div key={id} className="df aic jcsb py0-75rem cp" style={{ borderBottom: '1px solid #F5F6F9' }} onClick={() => toggleMod(id)}>
                                        <div className="df fdc" style={{ flex: 1, minWidth: 0 }}>
                                            <span className="fs0-75rem w100 tal fw500 c15">{mod['moduleName']}</span>
                                            <span className="fs0-75rem w100 tal fw400" style={{ color: isStarted ? '#09B255' : '#AAACB5', marginTop: 2 }}>
                                                {isStarted ? `${answered} question${answered !== 1 ? 's' : ''} answered` : 'Not started yet'}
                                            </span>
                                        </div>
                                        <div className="df aic jcc br5px ml1rem" style={{ width: 18, height: 18, border: `2px solid ${checked ? '#4A67FF' : '#D9D9D9'}`, backgroundColor: checked ? '#4A67FF' : '#fff', flexShrink: 0 }}>
                                            {checked && <Check style={{ fontSize: 12, color: '#fff' }} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="df aic jcsb px2rem py1-5rem" style={{ borderTop: '1px solid #DDE2F2' }}>
                            <button className="fs0-875rem fw500 c12 cp" style={{ background: 'none', border: 'none' }} onClick={onClose}>Cancel</button>
                            <button
                                className="df aic br50px py0-5rem px1-5rem fs0-875rem fw500 cp"
                                style={{ backgroundColor: preGenSelectedIds.size ? '#4A67FF' : '#D9D9D9', color: '#fff', border: 'none', opacity: preGenSelectedIds.size ? 1 : 0.6, cursor: preGenSelectedIds.size ? 'pointer' : 'default' }}
                                onClick={handleConfirmGenerate}
                                disabled={!preGenSelectedIds.size}
                            >
                                Generate {preGenSelectedIds.size ? `(${preGenSelectedIds.size} module${preGenSelectedIds.size !== 1 ? 's' : ''})` : ''}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function WorkflowMasterSummary({ company, setTab }) {
    const companyId = company?.['_id'];
    const [items, setItems] = useState([]);
    const [modules, setModules] = useState([]);
    const [assignees, setAssignees] = useState([]);
    const [selectedModule, setSelectedModule] = useState(null);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [loadState, setLoadState] = useState('loading'); // 'loading' | 'empty' | 'data'
    const [isGenerating, setIsGenerating] = useState(() => readWfProgress(company?.['_id'])?.generating === true);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadProgress, setLoadProgress] = useState(0);
    const [showModuleSelectModal, setShowModuleSelectModal] = useState(false);
    const [modalProgress, setModalProgress] = useState(null); // { moduleId: answeredCount }
    const [modalProgressLoading, setModalProgressLoading] = useState(false);
    const [preGenSelectedIds, setPreGenSelectedIds] = useState(new Set());
    const [genHttpDone, setGenHttpDone] = useState(false);

    useEffect(() => {
        if (!openDropdown) return;
        const handler = (e) => { if (!e.target.closest('[data-dropdown]')) setOpenDropdown(null); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openDropdown]);

    const fetchData = useCallback(async () => {
        const companyId = company?.['_id'];
        if (!companyId) return;
        setLoadState('loading');
        try {
            const [modulesRes, answersRes, assigneesRes] = await Promise.all([
                getRequest(`/modules/temp-modules?limit=100&isActive=true&companyId=${companyId}`),
                getRequest(`/modules/workflow/pending-answers?companyId=${companyId}`),
                getRequest(`/user/company-assignees/${companyId}`)
            ]);
            if (modulesRes?.['body']?.['modules']) setModules(modulesRes['body']['modules']);
            if (assigneesRes?.['body']) setAssignees(assigneesRes['body']);
            const answers = answersRes?.['body'] || [];
            setItems(answers);
            setLoadState(answers.length ? 'data' : 'empty');
        } catch (_) {
            setLoadState('empty');
        }
    }, [company]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (loadState !== 'loading') { setLoadProgress(0); return; }
        setLoadProgress(0);
        const interval = setInterval(() => {
            setLoadProgress(prev => {
                if (prev >= 85) { clearInterval(interval); return 85; }
                return prev + (85 - prev) * 0.08;
            });
        }, 120);
        return () => clearInterval(interval);
    }, [loadState]);

    useEffect(() => {
        const saved = localStorage.getItem('priorityManager');
        if (!saved || !modules.length) return;
        const match = modules.find(m => m['moduleName'] === saved || m['moduleName']?.toLowerCase() === saved?.toLowerCase());
        if (match) setSelectedModule(match);
        localStorage.removeItem('priorityManager');
    }, [modules]);

    useEffect(() => {
        if (loadState === 'loading' || !modules.length) return;
        const flag = localStorage.getItem('openModuleSelectModal');
        if (!flag) return;
        localStorage.removeItem('openModuleSelectModal');
        openModuleSelectModal();
    }, [loadState, modules]);

    useEffect(() => { setCurrentPage(1); }, [selectedModule]);

    const handleGenerationComplete = useCallback(async () => {
        clearWfProgress(companyId);
        await fetchData();
        setIsGenerating(false);
    }, [fetchData, companyId]);

    const handleGenerationError = useCallback(() => {
        clearWfProgress(companyId);
        setIsGenerating(false);
    }, [companyId]);

    const openModuleSelectModal = async (forceModuleId = null) => {
        if (!companyId || isGenerating) return;
        setShowModuleSelectModal(true);
        setModalProgressLoading(true);
        try {
            const res = await getRequest(`/modules/workflow/module-progress?companyId=${companyId}`);
            const progressList = res?.['body'] || [];
            const map = {};
            progressList.forEach(p => { map[p['moduleId']] = p['answeredCount']; });
            setModalProgress(map);
            if (forceModuleId) {
                setPreGenSelectedIds(new Set([forceModuleId.toString()]));
            } else {
                const eligibleMods = getEligibleModules();
                const withProgress = new Set(
                    eligibleMods.filter(m => (map[m['_id']] || 0) > 0).map(m => m['_id'].toString())
                );
                setPreGenSelectedIds(withProgress.size > 0 ? withProgress : new Set(eligibleMods.map(m => m['_id'].toString())));
            }
        } catch (_) {
            setModalProgress({});
            const eligibleMods = getEligibleModules();
            setPreGenSelectedIds(new Set(eligibleMods.map(m => m['_id'].toString())));
        } finally {
            setModalProgressLoading(false);
        }
    };

    const getEligibleModules = () => modules.filter(
        mod =>
            mod.moduleName !== 'KPI Report' &&
            (mod.moduleName !== 'Investment Readiness' || company?.testType === 'Investment') &&
            (!mod.isHealthBoost || (company?.testType && company?.testType.toLowerCase() === 'growth')) &&
            (!mod.isPaid || mod.hasPurchased)
    );

    const handleConfirmGenerate = async () => {
        if (!companyId || isGenerating || !preGenSelectedIds.size) return;
        setShowModuleSelectModal(false);
        setIsGenerating(true);
        setGenHttpDone(false);
        const moduleIds = [...preGenSelectedIds];
        writeWfProgress(companyId, { generating: true, moduleIds, startedAt: Date.now(), phase: 'waiting', modules: [] });
        try {
            const res = await postRequest('/modules/workflow/generate', { companyId, moduleIds }, true);
            if (res?.['code'] !== 200) {
                showMessage('Failed to generate workflow');
                clearWfProgress(companyId);
                setIsGenerating(false);
            } else {
                setGenHttpDone(true);
            }
        } catch (_) {
            showMessage('Failed to generate workflow');
            clearWfProgress(companyId);
            setIsGenerating(false);
        }
    };

    const handleDirectGenerate = async (moduleId) => {
        if (!companyId || isGenerating || !moduleId) return;
        setIsGenerating(true);
        setGenHttpDone(false);
        const moduleIds = [moduleId.toString()];
        writeWfProgress(companyId, { generating: true, moduleIds, startedAt: Date.now(), phase: 'waiting', modules: [] });
        try {
            const res = await postRequest('/modules/workflow/generate', { companyId, moduleIds }, true);
            if (res?.['code'] !== 200) {
                showMessage('Failed to generate workflow');
                clearWfProgress(companyId);
                setIsGenerating(false);
            } else {
                setGenHttpDone(true);
            }
        } catch (_) {
            showMessage('Failed to generate workflow');
            clearWfProgress(companyId);
            setIsGenerating(false);
        }
    };

    const handleNavigateToQuestion = (item) => {
        if (!setTab) return;
        const sectionQNo = item['qNo']?.split('.')?.[0];
        const mod = modules.find(m => m['_id']?.toString() === item['moduleId']?.toString());
        const moduleSlug = mod?.['slug'];
        if (!moduleSlug || !sectionQNo) return;
        localStorage.setItem('lastReportAnchor', sectionQNo);
        setTab(moduleSlug);
        let attempts = 0;
        const poll = setInterval(() => {
            attempts++;
            const el = document.getElementById(sectionQNo);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                localStorage.removeItem('lastReportAnchor');
                clearInterval(poll);
            } else if (attempts >= 30) {
                localStorage.removeItem('lastReportAnchor');
                clearInterval(poll);
            }
        }, 300);
    };

    const PRIORITY_TO_TASK_PRIORITY = { 'High': 'High Priority', 'Medium': 'Medium Priority', 'Low': 'Low Priority' };
    const WORKFLOW_STATUS_TO_TEMP_STATUS = { 'Not Started': 'Not Started', 'In Progress': 'In Progress', 'Ready For Review': 'Ready for Review', 'Completed': 'Completed', 'Not Applicable': 'Not Applicable' };

    const updateItem = async (id, field, value) => {
        const item = items.find(i => i['_id'] === id);
        setItems(prev => prev.map(i => i['_id'] === id ? { ...i, [field]: value } : i));
        setOpenDropdown(null);

        try { await putRequest(`/modules/workflow/pending-answers/${id}`, { [field]: value }, true); } catch (_) { /* optimistic */ }

        if (field === 'priority' || field === 'workflowStatus') {
            const answerUpdate = {};
            if (field === 'priority' && PRIORITY_TO_TASK_PRIORITY[value]) answerUpdate['taskPriority'] = PRIORITY_TO_TASK_PRIORITY[value];
            if (field === 'workflowStatus' && WORKFLOW_STATUS_TO_TEMP_STATUS[value] !== undefined) answerUpdate['status'] = WORKFLOW_STATUS_TO_TEMP_STATUS[value];
            if (Object.keys(answerUpdate).length && item?.['answerId']) {
                try { await putRequest(`/modules/temp-answers/${item['answerId']}`, { companyId: company?.['_id'], answer: answerUpdate }, true); } catch (_) { /* non-blocking */ }
            }
        }
    };

    const PriorityDropdown = ({ item }) => {
        const key = `priority-${item['_id']}`;
        const isOpen = openDropdown === key;
        const selected = PRIORITY_OPTIONS.find(o => o.value === item['priority']);
        return (
            <div className="pr df jcfs" data-dropdown style={{ display: 'inline-flex' }}>
                <button
                    className="df aic jcc br50px fw500 cp wsn"
                    data-dropdown
                    style={{ backgroundColor: selected?.bg || '#F5F6F9', color: selected?.color || '#505050', border: 'none', gap: '5px', fontSize: '11px', padding: '4px 12px', minWidth: '120px' }}
                    onClick={() => setOpenDropdown(isOpen ? null : key)}
                >
                    {selected?.label || 'Priority'} <KeyboardArrowDown style={{ fontSize: 14 }} />
                </button>
                {isOpen && (
                    <div className="pa bg1" data-dropdown style={{ top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 300, minWidth: '160px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #DDE2F2' }}>
                        {PRIORITY_OPTIONS.map(opt => (
                            <div key={opt.value} data-dropdown className="cp df aic jcc fw500 wsn"
                                style={{ backgroundColor: opt.bg, color: opt.color, padding: '8px 14px', fontSize: '12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                                onClick={() => updateItem(item['_id'], 'priority', opt.value)}
                            >{opt.label}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const StatusDropdown = ({ item }) => {
        const key = `status-${item['_id']}`;
        const isOpen = openDropdown === key;
        const selected = STATUS_OPTIONS.find(o => o.value === item['workflowStatus']);
        return (
            <div className="pr df jcfs" data-dropdown style={{ display: 'inline-flex' }}>
                <button
                    className="df aic jcfs br50px fw500 cp wsn"
                    data-dropdown
                    style={{ backgroundColor: selected?.bg || '#F5F6F9', color: selected?.color || '#505050', border: selected?.border || 'none', gap: '5px', fontSize: '11px', padding: '4px 12px', minWidth: '130px' }}
                    onClick={() => setOpenDropdown(isOpen ? null : key)}
                >
                    {selected?.check && <span style={{ marginRight: '2px' }}>✓</span>}
                    {selected?.label || 'Status'} <KeyboardArrowDown style={{ fontSize: 14 }} />
                </button>
                {isOpen && (
                    <div className="pa bg1" data-dropdown style={{ top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 300, minWidth: '170px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #DDE2F2' }}>
                        <div className="df aic jcsb" style={{ backgroundColor: '#F5F6F9', padding: '8px 14px', borderBottom: '1px solid #DDE2F2' }}>
                            <span className="fs12px fw500" style={{ color: '#505050' }}>Status</span>
                            <i className="fas fa-chevron-down" style={{ fontSize: '9px', color: '#969696' }} />
                        </div>
                        {STATUS_OPTIONS.map(opt => (
                            <div key={opt.value} data-dropdown className="cp df aic jcc fw500 wsn"
                                style={{ backgroundColor: opt.bg, color: opt.color, padding: '8px 14px', fontSize: '12px', border: opt.border || 'none', borderTop: '1px solid rgba(0,0,0,0.06)' }}
                                onClick={() => updateItem(item['_id'], 'workflowStatus', opt.value)}
                            >
                                {opt.check && <span style={{ marginRight: '4px' }}>✓</span>}
                                {opt.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const ROLE_COLORS = { 'Owner': '#4A67FF', 'Member': '#038589', 'Advisor': '#F0AE0D', 'Investor': '#09B255' };

    const AssignedToDropdown = ({ item }) => {
        const key = `assignedTo-${item['_id']}`;
        const isOpen = openDropdown === key;
        const assignedName = item['assignedTo'] || '';
        const sectionNo = item['qNo']?.split('.')?.[0] || '';

        const hasPermission = (a) => {
            // Investor owner (no createdBy) always has access regardless of configured permissions
            if (a['companyRole'] === 'Investor' && !a['createdBy']) return true;
            // null means full access (owner / servicedBy)
            if (a['companyPermissions'] === null) return true;
            if (!sectionNo) return true;
            // companyPermissions is the modules map: { DRN001: true, DRN004: false, ... }
            return !!a['companyPermissions']?.[sectionNo];
        };

        const eligibleAssignees = assignees.filter(a => {
            if (!hasPermission(a)) return false;
            const displayName = `${a['firstName'] || ''} ${a['lastName'] || ''}`.trim() || a['email'] || '';
            return !!displayName;
        });

        return (
            <div className="pr df jcfs" data-dropdown style={{ display: 'inline-flex' }}>
                <button
                    className="df aic jcsb br50px fw500 cp wsn"
                    data-dropdown
                    style={{ backgroundColor: '#fff', color: '#151313', border: '1px solid #D9D9D9', gap: '6px', fontSize: '11px', padding: '5px 14px', minWidth: '130px' }}
                    onClick={() => setOpenDropdown(isOpen ? null : key)}
                >
                    <span style={{ flex: 1, textAlign: 'left' }}>{assignedName || 'Assign to…'}</span>
                    <KeyboardArrowDown style={{ fontSize: 16, color: '#4A67FF', flexShrink: 0 }} />
                </button>
                {isOpen && (
                    <div className="pa bg1" data-dropdown style={{ top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 300, minWidth: '200px', maxHeight: '220px', overflowY: 'auto', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #DDE2F2' }}>
                        <div className="df aic" style={{ backgroundColor: '#F5F6F9', padding: '8px 14px', borderBottom: '1px solid #DDE2F2' }}>
                            <span className="fs12px fw500" style={{ color: '#505050' }}>Assign To</span>
                        </div>
                        {eligibleAssignees.length === 0 && (
                            <div className="tac py0-75rem fs12px c12">No eligible assignees</div>
                        )}
                        {eligibleAssignees.map(a => {
                            const name = `${a['firstName'] || ''} ${a['lastName'] || ''}`.trim() || a['email'];
                            const roleColor = ROLE_COLORS[a['companyRole']] || '#969696';
                            return (
                                <div key={a['_id']} data-dropdown className="cp df aic jcsb px1rem py0-5rem"
                                    style={{ borderBottom: '1px solid #F5F6F9', backgroundColor: assignedName === name ? '#F0F2FD' : 'transparent' }}
                                    onClick={() => updateItem(item['_id'], 'assignedTo', name)}
                                >
                                    <span className="fs12px fw400 c15">{name}</span>
                                    <span className="fs10px fw500 br50px" style={{ backgroundColor: roleColor + '18', color: roleColor, padding: '2px 7px', marginLeft: '6px', flexShrink: 0 }}>{a['companyRole']}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };


    const displayedItems = selectedModule ? items.filter(i => i['moduleName'] === selectedModule['moduleName']) : items;
    const completedCount = displayedItems.filter(i => i['workflowStatus'] === 'Ready For Review').length;
    const completionPct = displayedItems.length ? Math.round((completedCount / displayedItems.length) * 100) : 0;

    const totalPages = Math.ceil(displayedItems.length / ITEMS_PER_PAGE);
    const paginatedItems = displayedItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const getPaginationPages = () => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
        if (currentPage >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    };

    const Pagination = () => {
        if (totalPages <= 1) return null;
        const isFirst = currentPage === 1;
        const isLast = currentPage === totalPages;
        return (
            <div className="df aic jcc w100 mb2rem">
                <div className="df aic" style={{ backgroundColor: '#F0F0F8', borderRadius: '50px', padding: '5px 6px', gap: '3px' }}>
                    <button
                        className="df aic jcc fs12px fw500"
                        style={{ backgroundColor: '#fff', borderRadius: '50px', padding: '5px 14px', border: 'none', color: '#505050', opacity: isFirst ? 0.4 : 1, cursor: isFirst ? 'default' : 'pointer' }}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={isFirst}
                    >
                        {'< Back'}
                    </button>
                    {getPaginationPages().map((page, i) => page === '...'
                        ? <span key={`ellipsis-${i}`} className="df aic jcc fs12px fw400 c12" style={{ width: '28px', height: '28px' }}>...</span>
                        : (
                            <button
                                key={page}
                                className="df aic jcc fw500 fs12px cp"
                                style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: currentPage === page ? '#4A67FF' : 'transparent', color: currentPage === page ? '#fff' : '#151313', flexShrink: 0 }}
                                onClick={() => setCurrentPage(page)}
                            >
                                {page}
                            </button>
                        )
                    )}
                    <button
                        className="df aic jcc fs12px fw500"
                        style={{ backgroundColor: isLast ? '#fff' : '#4A67FF', borderRadius: '50px', padding: '5px 14px', border: 'none', color: isLast ? '#505050' : '#fff', opacity: isLast ? 0.4 : 1, cursor: isLast ? 'default' : 'pointer' }}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={isLast}
                    >
                        {'Next >'}
                    </button>
                </div>
            </div>
        );
    };

    if (isGenerating) return <WorkflowGenerationProgress companyId={companyId} onComplete={handleGenerationComplete} onError={handleGenerationError} httpDone={genHttpDone} />;

    if (loadState === 'loading') return (
        <div className="sectionWrapper py1rem anylist-builder-tab df fdc aic jcc" style={{ minHeight: '300px' }}>
            <div style={{ width: '50px', height: '50px' }} className="mb1rem">
                <CircularProgressbar value={loadProgress} text="" styles={buildStyles({ pathColor: '#FC7785', trailColor: '#F9DEDE', pathTransitionDuration: 0.3 })} />
            </div>
            <p className="fs1rem fw400 c12">Loading workflow data…</p>
        </div>
    );

    if (loadState === 'empty') return (
        <div className="sectionWrapper py1rem anylist-builder-tab df fdc aic jcc tac" style={{ minHeight: '350px' }}>
            <img src={require('../../../../images/featureWorkflow/warning icon.svg')} alt="" style={{ width: '60px', marginBottom: '1.5rem', opacity: 0.35 }} />
            <h3 className="fs1-25rem fw600 c15 mb0-5rem">No Workflow Data Yet</h3>
            <p className="fs0-875rem fw400 c12 mb2rem" style={{ maxWidth: '420px' }}>
                Click <strong>Generate</strong> to analyse your due diligence modules and surface the unanswered questions that investors will scrutinise most.
            </p>
            <button
                className="df aic br50px py0-75rem px2rem bg1 bss bw1px bo29 c15 fs0-875rem fw500 cp"
                onClick={() => openModuleSelectModal()}
                disabled={isGenerating}
            >
                <img src={require('../../../../images/featureWorkflow/regenerate.svg')} alt="Generate" className="w20px h20px mr0-5rem" />
                Generate Workflow
            </button>
            {showModuleSelectModal && <ModuleSelectModal eligibleMods={getEligibleModules()} preGenSelectedIds={preGenSelectedIds} setPreGenSelectedIds={setPreGenSelectedIds} modalProgressLoading={modalProgressLoading} modalProgress={modalProgress} handleConfirmGenerate={handleConfirmGenerate} onClose={() => setShowModuleSelectModal(false)} />}
        </div>
    );

    return (
        <div className="sectionWrapper py1rem fs0-75rem pr anylist-builder-tab">
            <div className="df aic jcc w100 mb1-5rem mt1rem">
                <h3 className="fs1-25rem fw600 c15">Priority Manager — Master Summary</h3>
            </div>
            <div className="df aife jcsb w100 mb1-5rem">
                {/* Module selector dropdown */}
                <div className="df aife">
                    <div className="df aic mb0rem pr" data-dropdown>
                        <div
                            className="df aic cp br50px py0-5rem px1rem bss bw1px bo29 c15 fs0-875rem fw500"
                            style={{ width: 'fit-content', userSelect: 'none' }}
                            data-dropdown
                            onClick={() => setOpenDropdown(openDropdown === 'module-selector' ? null : 'module-selector')}
                        >
                            {selectedModule
                                ? <>{selectedModule['moduleName']}</>
                                : 'Master Summary'
                            }
                            <KeyboardArrowDown sx={{ color: '#4b66fe', ml: 0.5, fontSize: 18 }} />
                        </div>
                        {openDropdown === 'module-selector' && (
                            <div className="pa bg1 br15px shadow" style={{ top: 'calc(100% + 8px)', left: 0, minWidth: '240px', maxHeight: '300px', overflowY: 'auto', zIndex: 100, border: '1px solid #DDE2F2' }} data-dropdown>
                                <div className="df aic px1rem py1rem cp hoverAllModule" data-dropdown
                                    style={{ borderBottom: '1px solid #F5F6F9' }}
                                    onClick={() => { setSelectedModule(null); setOpenDropdown(null); }}
                                >
                                    <span className="fs12px fw500 c3">Master Summary (All)</span>
                                </div>
                                {modules.filter(
                                    mod =>
                                        mod.moduleName !== "KPI Report" &&
                                        (mod.moduleName !== "Investment Readiness" ||
                                            company?.testType === "Investment") &&
                                        (!mod.isHealthBoost || (company?.testType && company?.testType.toLowerCase() === "growth")) &&
                                        (!mod.isPaid || mod.hasPurchased)
                                ).map(mod => (
                                    <div key={mod['_id']} className="df aic px1rem py0-5rem cp hoverModule" data-dropdown
                                        style={{ borderBottom: '1px solid #F5F6F9' }}
                                        onClick={() => { setSelectedModule(mod); setOpenDropdown(null); }}
                                    >
                                        <span className="fs12px fw400 c15">{mod['moduleName']}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Generate button + completeness ring */}
                <div className="df aife">
                    <button
                        className="df aic br50px py0-5rem px1rem bg1 bss bw1px bo29 c15 fs0-875rem fw500 cp"
                        style={{ opacity: isGenerating ? 0.6 : 1 }}
                        onClick={() => selectedModule ? handleDirectGenerate(selectedModule['_id']) : openModuleSelectModal()}
                        disabled={isGenerating}
                    >
                        <img src={require('../../../../images/featureWorkflow/regenerate.svg')} alt="Regenerate" className="w20px h20px mr0-5rem" />
                        {isGenerating ? 'Generating…' : selectedModule ? `Regenerate Module` : 'Regenerate'}
                    </button>
                    {/* <div className="df fdc aic">
                        <div style={{ width: '72px', height: '72px' }}>
                            <CircularProgressbar
                                value={completionPct}
                                text={`${completionPct}%`}
                                styles={buildStyles({ textSize: '22px', pathColor: '#FC7785', textColor: '#151313', trailColor: '#F9DEDE', strokeLinecap: 'round', pathTransitionDuration: 0.5 })}
                            />
                        </div>
                        <span className="fs12px fw500 c15 tac mt0-5rem wsn">Priority List Completeness</span>
                    </div> */}
                </div>
            </div>

            {/* Data table or per-module empty state */}
            {displayedItems.length === 0 ? (
                <div className="mt1rem br25px w100 mb1-5rem bsbb bg1 df fdc aic jcc tac" style={{ border: '1px solid #DDE2F2', minHeight: '280px', padding: '3rem 2rem' }}>
                    {selectedModule ? (
                        <>
                            <img src={require('../../../../images/featureWorkflow/warning icon.svg')} alt="" style={{ width: '48px', marginBottom: '1.25rem', opacity: 0.3 }} />
                            <h4 className="fs1rem fw600 c15 mb0-5rem">No Priority Items for {selectedModule['moduleName']}</h4>
                            <p className="fs0-875rem fw400 c12 mb1-5rem" style={{ maxWidth: '400px' }}>
                                This module may have no outstanding issues, or its priority list hasn't been generated yet. Click below to generate it now.
                            </p>
                            <button
                                className="df aic br50px py0-75rem px2rem bg1 bss bw1px bo29 c15 fs0-875rem fw500 cp"
                                onClick={() => handleDirectGenerate(selectedModule['_id'])}
                                disabled={isGenerating}
                                style={{ opacity: isGenerating ? 0.6 : 1 }}
                            >
                                <img src={require('../../../../images/featureWorkflow/regenerate.svg')} alt="Generate" className="w20px h20px mr0-5rem" />
                                Generate for {selectedModule['moduleName']}
                            </button>
                        </>
                    ) : (
                        <>
                            <img src={require('../../../../images/featureWorkflow/warning icon.svg')} alt="" style={{ width: '48px', marginBottom: '1.25rem', opacity: 0.3 }} />
                            <h4 className="fs1rem fw600 c15 mb0-5rem">No Priority Items Found</h4>
                            <p className="fs0-875rem fw400 c12 mb1-5rem" style={{ maxWidth: '400px' }}>
                                No outstanding issues were found across your modules. You can regenerate to refresh the analysis.
                            </p>
                            <button
                                className="df aic br50px py0-75rem px2rem bg1 bss bw1px bo29 c15 fs0-875rem fw500 cp"
                                onClick={() => openModuleSelectModal()}
                                disabled={isGenerating}
                                style={{ opacity: isGenerating ? 0.6 : 1 }}
                            >
                                <img src={require('../../../../images/featureWorkflow/regenerate.svg')} alt="Regenerate" className="w20px h20px mr0-5rem" />
                                Regenerate Workflow
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div className="mt1rem px2rem py2rem br25px w100 mb1-5rem bsbb bg1" style={{ border: '1px solid #DDE2F2' }}>
                    <div className="w100" style={{ overflowX: 'auto' }}>
                        <table className="w100" style={{ borderCollapse: 'collapse', minWidth: '1100px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F5F6F9', borderBottom: '2px solid #DDE2F2' }}>
                                    {TABLE_COLS.map(col => (
                                        <th key={col} className="fs11px fw600 c15 ttu tal py0-875rem px1rem wsn tac"
                                            style={{ letterSpacing: '0.03em', minWidth: ['Priority', 'Status', 'Criticality'].includes(col) ? '120px' : '100px' }}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedItems.map((item, idx) => (
                                    <tr key={item['_id']} style={{ borderBottom: '1px solid #DDE2F2', backgroundColor: idx % 2 === 0 ? '#fff' : '#FAFBFF' }}>
                                        <td className="py0-875rem px0-875rem cp" style={{ minWidth: '130px' }} onClick={() => handleNavigateToQuestion(item)}>
                                            <p className="fs12px fw600 c3 mb0-25rem tal">{item['moduleName']}</p>
                                            <p className="fs11px fw400 c12 tal">{item['qNo']}</p>
                                        </td>
                                        <td className="py0-875rem px0-875rem tal" style={{ minWidth: '160px', maxWidth: '180px' }}>
                                            <p className="fs11px fw500 c3">{item['issueName'] || item['qDescription']}</p>
                                        </td>
                                        <td className="py0-875rem px0-875rem" style={{ minWidth: '180px', maxWidth: '200px' }}>
                                            <p className="fs12px fw400 c14 tal">{item['reason']}</p>
                                        </td>
                                        <td className="py0-875rem px0-875rem" style={{ minWidth: '160px', maxWidth: '180px' }}>
                                            <p className="fs12px fw400 c14 tal">{item['investorLens']}</p>
                                        </td>
                                        <td className="py0-875rem px0-875rem tac" style={{ minWidth: '80px' }}>
                                            {getCriticalityIcon(item['criticality'])}
                                        </td>
                                        <td className="py0-875rem px0-875rem" style={{ minWidth: '180px', maxWidth: '200px' }}>
                                            <p className="fs12px fw400 c14 tal">{item['actionRequired']}</p>
                                        </td>
                                        <td className="py0-875rem px0-875rem tal" style={{ minWidth: '130px' }}>
                                            <PriorityDropdown item={item} />
                                        </td>
                                        <td className="py0-875rem px0-875rem tal" style={{ minWidth: '150px' }}>
                                            <AssignedToDropdown item={item} />
                                        </td>
                                        <td className="py0-875rem px0-875rem tal" style={{ minWidth: '140px' }}>
                                            <StatusDropdown item={item} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {/* <Pagination /> */}

            {showModuleSelectModal && <ModuleSelectModal eligibleMods={getEligibleModules()} preGenSelectedIds={preGenSelectedIds} setPreGenSelectedIds={setPreGenSelectedIds} modalProgressLoading={modalProgressLoading} modalProgress={modalProgress} handleConfirmGenerate={handleConfirmGenerate} onClose={() => setShowModuleSelectModal(false)} />}
        </div>
    );
}
